// Content data — easily editable. Safe for static export.

export const site = {
  name: "Himansshu Agrawal",
  tagline: "Decarbonization Expert",
  brand: "REConsultX",
  url: "www.reconsultx.com",
  email: "himanshu@reconsultx.com",
  phone: "+1 814 446 2640",
  linkedin: "https://www.linkedin.com/in/himansshu-agrawal-renewables",
  regions: ["APAC", "Africa", "North America", "Australia", "Europe"],
};

export const heroStats = [
  { value: 13.7, unit: "B+", prefix: "$", label: "Project Value Delivered", decimals: 1 },
  { value: 10.6, unit: "GWp", label: "Solar PV", decimals: 1 },
  { value: 3.0,  unit: "GW",  label: "Wind", decimals: 1 },
  { value: 5.4,  unit: "GWh", label: "Energy Storage", decimals: 1 },
  { value: 15,   unit: "",    label: "Countries", decimals: 0 },
  { value: 15,   unit: "+",   label: "Years of Experience", decimals: 0 },
];

export const bio = {
  short:
    "Award-winning decarbonization expert with 15+ years delivering $13.7B+ of utility-scale renewable energy and hybrid storage projects across 15 countries.",
  long: [
    "I help heavy industry decarbonize — profitably. My work bridges technical design, commercial due-diligence, financial modelling, and energy-markets integration to move Gigawatts of renewable capacity from proposal to operation.",
    "As Vice President at Serentica Renewables — after providing consultancy to them for two years — I set up their energy-modelling team and built state-of-the-art renewable-hybridization practices to tackle the toughest challenge in clean energy delivery: providing firm, time-block-dispatchable, commercially viable renewable generation. I built the data-analytics backbone that supported equity-cum-debt investment decisions exceeding INR 15,000 crore, and enabled PPAs for more than 3 GW of PV-cum-Wind and 1.5 GWh of Energy Storage for customers in the hard-to-abate mining sector.",
    "This decarbonization-focused energy-modelling work was at the forefront of global energy-modelling practices and was rigorously vetted, endorsed and signed-off by industry leaders and stakeholders across investment, project-finance, business development, project-execution and operations. Over time, I became the trusted internal voice on applied energy-modelling and an independent thought leader on solving decarbonization challenges for industrial clients.",
    "As founder of REConsultX, I partner with developers, IPPs, industrial off-takers and financiers on hybrid renewable strategy, technology assessment and project due-diligence. Earlier at EM-ONE I initiated and managed global designs that reduced cost, shortened cycle times and improved yield for their solar micro-grid solutions — designs that were adopted into EM-ONE's standard basis-of-design packages across platform architectures and market segments. At SunEdison I led design and engineering across Japan, APAC project acquisition, and fleet performance for 650 MW of operating assets.",
    "Harvard Business School CORE — Pass with High Honors. B.Tech (Electronics & Telecom), NIT Raipur — 8.55/10 CPI.",
  ],
};

export const experience = [
  {
    role: "Founder & CEO",
    company: "REConsultX",
    period: "Jun 2025 – Present",
    location: "Independent",
    summary:
      "Industrial decarbonization advisory — hybrid renewables, energy storage, energy markets, captive plants, grid & demand-side management. Techno/commercial due-diligence for PV, Wind, BESS and hybrid portfolios.",
  },
  {
    role: "Vice President",
    company: "Serentica Renewables",
    period: "Feb 2023 – May 2025",
    location: "India",
    summary:
      "Set up Serentica's energy-modelling team and built state-of-the-art renewable-hybridization practices for firm, time-block-dispatchable, commercially viable renewable generation. Built the data-analytics backbone that supported equity-cum-debt investment decisions exceeding INR 15,000 crore, and enabled PPAs for >3 GW of PV-cum-Wind and 1.5 GWh of Energy Storage for customers in the hard-to-abate mining sector. Over time became the trusted internal voice on applied energy-modelling and an independent thought leader on solving decarbonization challenges for industrial clients.",
  },
  {
    role: "Founder & CEO (first innings)",
    company: "REConsultX",
    period: "Jul 2021 – Feb 2023",
    location: "Independent",
    summary:
      "Industrial decarbonization with hybrid renewables, energy storage and energy markets. Techno/commercial due-diligence for hybrid renewables, PV and energy storage.",
  },
  {
    role: "Solutions Manager, Africa",
    company: "EM-ONE Energy Solutions",
    period: "Feb 2018 – Feb 2021",
    location: "Canada / Nigeria",
    summary:
      "Initiated and managed global designs aimed to reduce cost, shorten cycle times and improve yield of EM-ONE's solar micro-grid solutions. Identified, prioritized and executed innovation ideas across a range of solar and micro-grid platforms. Designs drew on international best practices and, once vetted, were adopted into EM-ONE's standard basis-of-design packages for respective platform architectures and market segments.",
  },
  {
    role: "Founder & CEO",
    company: "Shristi Solar",
    period: "Jul 2016 – Feb 2018",
    location: "India / Independent",
    summary:
      "End-to-end technical package delivery for PV. Developed hybrid energy solutions with ABB, SMA, Schneider and TESLA. D&E process creation. Cost control, benchmarking and optimisation. Team building and direct reporting to client CEOs.",
  },
  {
    role: "Asst. Manager, Fleet Performance — APAC",
    company: "SunEdison",
    period: "Jul 2015 – Feb 2018",
    location: "APAC",
    summary:
      "Opex & Capex analysis, fleet management for 650 MW PV/Wind fleet across APAC. Outage analysis, performance reporting and improvement. Black-belt in Operational Excellence.",
  },
  {
    role: "Asst. Manager, Project Acquisition — China, India, Australia, APAC",
    company: "SunEdison",
    period: "Jul 2014 – Jul 2015",
    location: "APAC",
    summary:
      "Techno-commercial due-diligence for PV and Wind acquisitions. Site audit, risk analysis, performance validation. Top 10 Talents in APAC (2015).",
  },
  {
    role: "Head, Design & Engineering",
    company: "SunEdison Japan",
    period: "Jul 2013 – Jul 2014",
    location: "Japan",
    summary:
      "PV design consultation, energy yield assessment, solar resource assessment, tendering support and project due-diligence. Implemented the world's first wireless monitoring system in a Japanese solar plant.",
  },
  {
    role: "Design Engineer — India, Malaysia",
    company: "SunEdison",
    period: "Jul 2011 – Jul 2013",
    location: "India / Malaysia",
    summary:
      "PV preliminary and detailed design, energy yield assessment. Delivered KLIA Airport, MAS Cargo and multiple flagship rooftop and utility-scale projects.",
  },
];

export const services = [
  {
    title: "Hybrid Renewable Energy Design",
    summary:
      "End-to-end design of hybrid plants combining PV, Wind, BESS, Pumped-Storage Hydro, Energy Markets participation, Grid interaction, Captive Generation, and Demand-Side Management — to deliver firm, round-the-clock, decarbonized power.",
    icon: "hybrid",
  },
  {
    title: "Techno-Commercial Due-Diligence",
    summary:
      "Independent due-diligence for PV, Wind, BESS and hybrid portfolios — acquisition support, lender's engineer scope, performance validation, risk flagging and mitigation.",
    icon: "magnifier",
  },
  {
    title: "Energy Modelling & System Sizing",
    summary:
      "Hour-by-hour simulations, time-block optimisation, oversizing and DC/AC ratio studies, technology stacking and scenario modelling for bids and internal investment decisions.",
    icon: "chart",
  },
  {
    title: "Industrial Decarbonization Strategy",
    summary:
      "Roadmaps for heavy industry — mines, smelters, cement, steel, O&G — to transition captive loads to RE with credible levelised cost, reliability and bankability.",
    icon: "leaf",
  },
  {
    title: "Energy Markets & RTC Product Structuring",
    summary:
      "Structuring firm, dispatchable and time-block renewable products — including the world's first time-block RE product — for IPPs, utilities and industrial off-takers.",
    icon: "block",
  },
  {
    title: "Technology & Supplier Qualification",
    summary:
      "Qualification, benchmarking and supplier down-selection for BESS, inverters, trackers, modules and balance-of-plant equipment across Tier-1 and emerging vendors.",
    icon: "check",
  },
  {
    title: "Fleet Performance & O&M Optimisation",
    summary:
      "Opex/Capex analytics, outage analysis, weather-data validation, performance reporting and targeted improvement programmes — proven across a 650 MW APAC fleet.",
    icon: "gauge",
  },
  {
    title: "EPC Management & Owner's Engineer",
    summary:
      "Owner-side EPC management — from scope freeze and tendering, through construction oversight, to commissioning and defect-liability handover.",
    icon: "hard-hat",
  },
];

// Sorted newest-first. Each entry is a single year for symmetric card layout.
export const certifications = [
  { label: "Harvard Business School CORE — Pass with High Honors", year: "2021" },
  { label: "TESLA Powerwall Training", year: "2020" },
  { label: "Certified Solar Designer (99% score) — SEDA Malaysia", year: "2020" },
  { label: "Winner — Solar Design Challenge Award, SCGJ Govt. of India", year: "2019" },
  { label: "TESLA Powerpack Training & Certification — Netherlands", year: "2019" },
  { label: "Certified Master Trainer in Solar PV — SCGJ, Govt. of India", year: "2017" },
  { label: "Top 10 Talents in APAC — SunEdison", year: "2015" },
  { label: "Black-belt Certified in Operational Excellence — SunEdison", year: "2015" },
  { label: "Certified Solar Designer — SEDA Malaysia", year: "2014" },
  { label: "Certified Solar Designer — ISPQ", year: "2013" },
];

export const achievements = [
  "80% decarbonization of a 2.5 GW mining load of a leading mining operator (Al / Cu / Zn) using PV, Wind, chemical & pumped hydro storage, and green electricity markets",
  "Developed the world's first time-block renewable product for industrial decarbonization",
  "Implemented the world's first wireless monitoring system in a solar PV plant — Japan, 2014",
  "Delivered $13.7 B+ of projects across 15 countries spanning Asia, Australia, Africa, North America and Europe",
];
